'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient, clearAuthStorage } from '@/lib/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string;
  role: 'applicant' | 'student' | 'teacher' | 'admin';
  phone: string;
  is_active: boolean;
}

interface AuthContextValue {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function isRefreshTokenError(error: any): boolean {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  const code = (error.code || error.error_code || '').toLowerCase();
  return (
    msg.includes('refresh token not found') ||
    msg.includes('invalid refresh token') ||
    msg.includes('refresh_token_not_found') ||
    code === 'refresh_token_not_found' ||
    code === 'bad_refresh_token'
  );
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const initialized = useRef(false);

  const clearAuthState = async (doSignOut = false) => {
    clearAuthStorage();
    if (doSignOut) {
      try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
    }
    setSession(null);
    setUser(null);
    setProfile(null);
    setLoading(false);
  };

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) return null;
      return data as UserProfile;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    let subscriptionRef: { unsubscribe: () => void } | null = null;

    const init = async () => {
      // Proactively probe the stored session ONCE on mount.
      // This surfaces "Invalid Refresh Token" immediately so we can clear storage
      // before onAuthStateChange fires with a stale session object.
      try {
        const { error: sessionError } = await supabase.auth.getSession();
        if (sessionError && isRefreshTokenError(sessionError)) {
          // Stale/revoked refresh token — wipe everything and stop here.
          // signOut({ scope: 'local' }) clears the local storage without a network call.
          await clearAuthState(true);
          return;
        }
      } catch (e: any) {
        if (isRefreshTokenError(e)) {
          await clearAuthState(true);
          return;
        }
      }

      // Session looks healthy — subscribe to ongoing auth state changes.
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
        if (event === 'SIGNED_OUT') {
          clearAuthStorage();
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        if (event === 'TOKEN_REFRESHED' && !currentSession) {
          await clearAuthState(true);
          return;
        }

        if (!currentSession) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setSession(currentSession);
        setUser(currentSession.user ?? null);

        if (currentSession.user) {
          const p = await fetchProfile(currentSession.user.id);
          setProfile(p);
        } else {
          setProfile(null);
        }
        setLoading(false);
      });

      subscriptionRef = subscription;
    };

    init();

    // Safety net: ensure loading spinner always resolves
    const safetyTimer = setTimeout(() => {
      setLoading((prev) => (prev ? false : prev));
    }, 5000);

    return () => {
      subscriptionRef?.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
          role: metadata?.role || 'applicant',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (user) {
      const p = await fetchProfile(user.id);
      setProfile(p);
    }
  };

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
